import { memo, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckSquare, Folder, Loader2, Search, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/ui";
import { usePaginatedDocuments } from "@/hooks/documents/useDocumentFetch";
import { useFolders } from "@/hooks/folders";
import { KnowledgeBase as KnowledgeBaseItem } from "@/types/knowledge/knowledge-base";
import { DocumentTypeIcon } from "./DocumentTypeIcon";

interface SearchableKnowledgeBaseProps {
  selectedDocuments: KnowledgeBaseItem[];
  onDocumentToggle: (doc: any, checked: boolean) => void;
  onBulkToggle?: (docs: any[], checked: boolean) => void;
  onDocumentsLoaded?: (documents: any[]) => void;
  maxHeight?: string;
  className?: string;
  disabled?: boolean;
  ownerId?: string;
}

const SearchableKnowledgeBaseComponent = ({
  selectedDocuments,
  onDocumentToggle,
  onBulkToggle,
  onDocumentsLoaded,
  maxHeight = "max-h-64",
  className = "",
  disabled = false,
  ownerId,
}: SearchableKnowledgeBaseProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
  const { user } = useAuth();

  // If ownerId is provided and differs from current user, query documents owned by them
  const isViewingOtherUserDocs = !!ownerId && !!user?.id && ownerId !== user.id;
  const effectiveUserId = isViewingOtherUserDocs ? ownerId : undefined;

  const { folders, isLoading: isLoadingFolders } = useFolders({ userId: effectiveUserId });

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginatedDocuments(
    "my-documents",
    20,
    {
      searchTerm: debouncedSearchTerm.trim() || undefined,
      folderId: selectedFolderId,
      readyOnly: true,
    },
    effectiveUserId,
  );

  const availableDocuments = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data]);
  const totalAvailable = data?.pages?.[0]?.total ?? availableDocuments.length;

  const unavailableSelectedDocuments = useMemo(() => {
    if (hasNextPage || isLoading || isFetchingNextPage) return [];
    const existingDocElIds = new Set(availableDocuments.map((doc) => doc.el_doc_id));
    return selectedDocuments.filter((selectedDoc) => !existingDocElIds.has(selectedDoc.id));
  }, [availableDocuments, selectedDocuments, hasNextPage, isLoading, isFetchingNextPage]);

  useEffect(() => {
    if (availableDocuments.length > 0 && onDocumentsLoaded) {
      onDocumentsLoaded(availableDocuments);
    }
  }, [availableDocuments, onDocumentsLoaded]);

  const allFolderDocsLinked = useMemo(() => {
    if (!selectedFolderId || availableDocuments.length === 0) return false;
    return availableDocuments.every((doc) =>
      selectedDocuments.some((selected) => selected.id === doc.el_doc_id),
    );
  }, [selectedFolderId, availableDocuments, selectedDocuments]);

  const handleBulkLinkFolder = () => {
    if (!onBulkToggle) {
      availableDocuments.forEach((doc) => {
        const isSelected = selectedDocuments.some((selected) => selected.id === doc.el_doc_id);
        if (!isSelected) {
          onDocumentToggle(doc, true);
        }
      });
      return;
    }

    const docsToToggle = availableDocuments.filter(
      (doc) => !selectedDocuments.some((selected) => selected.id === doc.el_doc_id),
    );
    if (docsToToggle.length > 0) {
      onBulkToggle(docsToToggle, true);
    }
  };

  const handleBulkUnlinkFolder = () => {
    if (!onBulkToggle) {
      availableDocuments.forEach((doc) => {
        const isSelected = selectedDocuments.some((selected) => selected.id === doc.el_doc_id);
        if (isSelected) {
          onDocumentToggle(doc, false);
        }
      });
      return;
    }

    const docsToToggle = availableDocuments.filter((doc) =>
      selectedDocuments.some((selected) => selected.id === doc.el_doc_id),
    );
    if (docsToToggle.length > 0) {
      onBulkToggle(docsToToggle, false);
    }
  };

  return (
    <div className={`space-y-3 ${className} relative`}>
      {disabled && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded">
          <div className="flex items-center gap-2 text-gray-600 bg-white px-4 py-2 rounded-lg shadow-sm border">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">Updating...</span>
          </div>
        </div>
      )}
      {/* Search Bar and Folder Filter */}
      <div className="flex gap-3">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search documents by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchTerm("")}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Folder Filter */}
        <Select
          value={selectedFolderId || "all"}
          onValueChange={(value) => setSelectedFolderId(value === "all" ? undefined : value)}
          disabled={isLoadingFolders}
        >
          <SelectTrigger className="w-fit min-w-[160px]">
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All folders" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All folders</SelectItem>
            {folders.map((folder) => (
              <SelectItem key={folder.id} value={folder.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: folder.color || "#3B82F6" }}
                  />
                  {folder.name} ({folder.document_count || 0})
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {selectedFolderId && availableDocuments.length > 0 && (
        <div className="flex gap-2">
          {!allFolderDocsLinked ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkLinkFolder}
              disabled={disabled}
              className="flex items-center gap-2 border-[#4E9CFF] text-[#4E9CFF] hover:bg-[#4E9CFF]/10"
            >
              <CheckSquare className="h-4 w-4" />
              Link all ({availableDocuments.length}) in folder
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkUnlinkFolder}
              disabled={disabled}
              className="flex items-center gap-2 border-[#FF4DB8] text-[#FF4DB8] hover:bg-[#FF4DB8]/10"
            >
              <X className="h-4 w-4" />
              Unlink all from folder
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading documents...</span>
        </div>
      ) : (
        <div
          className={`${maxHeight} overflow-auto rounded-lg ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        >
          {availableDocuments.length === 0 && unavailableSelectedDocuments.length === 0 ? (
            <div className="text-center text-gray-500 py-8 border-2 border-dashed border-gray-300 rounded-lg">
              {searchTerm ? "No documents found matching your search" : "No documents available"}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {unavailableSelectedDocuments.map((selectedDoc) => (
                <div
                  key={selectedDoc.id}
                  className="group relative flex items-start gap-3 p-4 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 opacity-70"
                >
                  <input
                    type="checkbox"
                    checked
                    onChange={() => onDocumentToggle({ el_doc_id: selectedDoc.id }, false)}
                    className="mt-0.5 w-4 h-4 accent-gray-400 cursor-pointer"
                    disabled={disabled}
                  />
                  <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                    <span
                      className="text-sm font-semibold truncate text-gray-500"
                      title={selectedDoc.name || selectedDoc.id}
                    >
                      {selectedDoc.name || selectedDoc.id}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-genie-warning">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span>Document unavailable</span>
                    </div>
                  </div>
                </div>
              ))}
              {availableDocuments.map((doc) => {
                const isSelected = selectedDocuments.some(
                  (selected) => selected.id === doc.el_doc_id,
                );
                return (
                  <label
                    key={doc.id}
                    className={`group relative flex items-start gap-3 p-4 cursor-pointer rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-transparent bg-gradient-to-r from-[#4E9CFF]/10 to-[#FF4DB8]/10 shadow-md"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute inset-0 rounded-lg p-[2px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] -z-10" />
                    )}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onDocumentToggle(doc, e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-blue-500 cursor-pointer"
                      disabled={disabled}
                    />
                    <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                      <span
                        className={`text-sm font-semibold truncate ${isSelected ? "text-gray-900" : "text-gray-700"}`}
                        title={doc.name || doc.id}
                      >
                        {doc.name || doc.id}
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <DocumentTypeIcon document={doc} showTooltip={false} iconSize="sm" />
                        <span className="text-xs text-gray-500">
                          {new Date(doc.created_at ?? Date.now()).toLocaleDateString()}
                        </span>
                        {doc.is_public && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-[#4E9CFF]/20 to-[#FF4DB8]/20 text-gray-700">
                            Public
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage || disabled}
                variant="outline"
                size="sm"
              >
                {isFetchingNextPage ? "Loading..." : "Load more documents"}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-gray-400">
        {totalAvailable > 0 && (
          <span>
            {searchTerm
              ? `${totalAvailable} documents found`
              : `${totalAvailable} documents available`}
            {" • "}
          </span>
        )}
        Select up to 20 documents from your knowledge base.
      </div>
    </div>
  );
};

export const SearchableKnowledgeBase = memo(SearchableKnowledgeBaseComponent);
